import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import OrganizationLayout from './OrganizationLayout';

/*
 * OrganizationSettingsPage — Phase 5 polish surface for an organization.
 *
 * Lets the Owner:
 *   1. Rename the workspace
 *   2. Migrate their own Personal tasks INTO this workspace (with a
 *      preview of how many would move + a visibility choice)
 *   3. Transfer ownership to another member (must already be a member)
 *   4. Delete the workspace (Stripe sub canceled; tasks fall back to
 *      Personal via the backend cascade)
 *
 * Admins see the rename + migration tools. Plain members see a read-only
 * summary. The page is mounted outside of MobileLayout so it shares the
 * same centered-max-width pattern as `/organization/welcome` and
 * `/organization/members`.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrganizationSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshWorkspaces, switchWorkspace } = useWorkspace();

  const orgIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('org_id');
  }, [location.search]);

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [preview, setPreview] = useState(null); // {total, by_category}
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState(null); // {kind: 'ok'|'err', text}

  // Local form state
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateVisibility, setMigrateVisibility] = useState('private');
  const [transferTo, setTransferTo] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const currentUserId = (() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw).id : null;
    } catch { return null; }
  })();
  const myRole = members.find((m) => m.user_id === currentUserId)?.role;
  const isOwner = myRole === 'owner';
  const isAdmin = myRole === 'admin' || isOwner;

  const loadAll = useCallback(async () => {
    if (!orgIdFromUrl) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [orgRes, memRes, prevRes] = await Promise.all([
        axios.get(`${API}/organizations/${orgIdFromUrl}`, { headers }),
        axios.get(`${API}/organizations/${orgIdFromUrl}/members`, { headers }),
        axios.get(`${API}/organizations/${orgIdFromUrl}/personal-tasks-preview`, { headers })
          .catch(() => ({ data: { total: 0, by_category: [] } })),
      ]);
      setOrg(orgRes.data);
      setMembers(memRes.data || []);
      setPreview(prevRes.data);
      setName(orgRes.data?.name || '');
    } catch (err) {
      setActionMsg({ kind: 'err', text: err?.response?.data?.detail || 'Could not load settings.' });
    } finally {
      setLoading(false);
    }
  }, [orgIdFromUrl]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRename = async () => {
    const trimmed = (name || '').trim();
    if (trimmed.length < 2 || trimmed === org?.name) return;
    setRenaming(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/organizations/${orgIdFromUrl}`,
        { name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrg(res.data);
      setName(res.data.name);
      try { await refreshWorkspaces(); } catch { /* noop */ }
      setActionMsg({ kind: 'ok', text: 'Workspace renamed.' });
    } catch (err) {
      setActionMsg({ kind: 'err', text: err?.response?.data?.detail || 'Rename failed.' });
    } finally {
      setRenaming(false);
    }
  };

  const handleMigrate = async () => {
    if (!preview || preview.total === 0) return;
    setMigrating(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/organizations/${orgIdFromUrl}/migrate-personal-tasks`,
        { visibility: migrateVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const count = res.data?.migrated_count || 0;
      setActionMsg({ kind: 'ok', text: `${count} task${count === 1 ? '' : 's'} moved to the team workspace.` });
      // Reload preview so the user sees 0 left and so the UI updates.
      await loadAll();
    } catch (err) {
      setActionMsg({ kind: 'err', text: err?.response?.data?.detail || 'Migration failed.' });
    } finally {
      setMigrating(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTo) return;
    if (!window.confirm('Transfer ownership? You will be demoted to admin and can no longer delete or rename the workspace.')) return;
    setTransferring(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/organizations/${orgIdFromUrl}/transfer-ownership`,
        { new_owner_user_id: transferTo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActionMsg({ kind: 'ok', text: 'Ownership transferred.' });
      setTransferTo('');
      await loadAll();
    } catch (err) {
      setActionMsg({ kind: 'err', text: err?.response?.data?.detail || 'Transfer failed.' });
    } finally {
      setTransferring(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/organizations/${orgIdFromUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cascaded = res.data?.tasks_returned_to_personal || 0;
      try { await refreshWorkspaces(); } catch { /* noop */ }
      try { switchWorkspace('personal'); } catch { /* noop */ }
      // Land on /tasks with a brief toast message via sessionStorage.
      try {
        sessionStorage.setItem(
          'edotask:toast',
          JSON.stringify({
            kind: 'ok',
            text: `Workspace deleted. ${cascaded} task${cascaded === 1 ? '' : 's'} moved back to Personal.`,
          })
        );
      } catch { /* noop */ }
      navigate('/');
    } catch (err) {
      setDeleting(false);
      setActionMsg({ kind: 'err', text: err?.response?.data?.detail || 'Delete failed.' });
    }
  };

  if (loading) {
    return (
      <OrganizationLayout org={null} active="settings" canManage={true}>
        <p className="text-gray-600">Loading settings…</p>
      </OrganizationLayout>
    );
  }

  if (!org) {
    return (
      <OrganizationLayout org={null} active="settings" canManage={true}>
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md text-center">
          <p className="text-gray-700 mb-4">Workspace not found or you don't have access.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium"
            data-testid="org-settings-back"
          >
            Back to Tasks
          </button>
        </div>
      </OrganizationLayout>
    );
  }

  const eligibleTransferTargets = members.filter((m) => m.role !== 'owner');
  const trialEnds = org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : null;

  return (
    <OrganizationLayout org={org} active="settings" canManage={isAdmin}>
      <div data-testid="org-settings-page">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{org.name} settings</h1>
          <p className="text-gray-600 text-sm mt-1">
            {org.subscription_status === 'trialing' && trialEnds
              ? `Trialing — trial ends ${trialEnds}`
              : org.subscription_status}
            {' · '}
            {org.seat_count} seat{org.seat_count === 1 ? '' : 's'}
          </p>
        </header>

        {actionMsg && (
          <div
            className={`mb-4 p-3 rounded-lg text-sm ${
              actionMsg.kind === 'ok'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
            data-testid="org-settings-action-msg"
          >
            {actionMsg.text}
          </div>
        )}

        {/* ---- Rename ---- */}
        {isAdmin && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Rename workspace</h2>
            <p className="text-sm text-gray-600 mb-3">
              Members will see the new name everywhere immediately.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={64}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Workspace name"
                data-testid="org-settings-rename-input"
              />
              <button
                onClick={handleRename}
                disabled={renaming || (name || '').trim().length < 2 || (name || '').trim() === org.name}
                className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="org-settings-rename-btn"
              >
                {renaming ? 'Saving…' : 'Save'}
              </button>
            </div>
          </section>
        )}

        {/* ---- Migrate personal tasks ---- */}
        {isAdmin && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Move my personal tasks here</h2>
            <p className="text-sm text-gray-600 mb-3">
              Bring your existing Personal-workspace tasks into <strong>{org.name}</strong> so
              you don't have to recreate them. Only YOUR tasks move — other members keep theirs.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Tasks ready to move</span>
                <span className="font-semibold text-gray-900" data-testid="org-migrate-total">
                  {preview?.total ?? 0}
                </span>
              </div>
              {preview?.by_category?.length > 0 && (
                <div className="text-xs text-gray-500 mt-2">
                  {preview.by_category.slice(0, 5).map((c, i) => (
                    <span key={c.category}>
                      {i > 0 ? ' · ' : ''}{c.category}: {c.count}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visibility after move
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMigrateVisibility('private')}
                  data-testid="org-migrate-private"
                  className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                    migrateVisibility === 'private'
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true">🔒</span>
                    <span>Keep private</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Only you see them</div>
                </button>
                <button
                  type="button"
                  onClick={() => setMigrateVisibility('team')}
                  data-testid="org-migrate-team"
                  className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                    migrateVisibility === 'team'
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true">🏢</span>
                    <span>Share with team</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">All members can see</div>
                </button>
              </div>
            </div>
            <button
              onClick={handleMigrate}
              disabled={migrating || !preview || preview.total === 0}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-2.5 rounded-lg font-medium hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              data-testid="org-migrate-btn"
            >
              {migrating
                ? 'Moving…'
                : preview?.total
                  ? `Move ${preview.total} task${preview.total === 1 ? '' : 's'} now`
                  : 'No personal tasks to move'}
            </button>
          </section>
        )}

        {/* ---- Transfer ownership ---- */}
        {isOwner && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Transfer ownership</h2>
            <p className="text-sm text-gray-600 mb-3">
              Promote another member to Owner. You'll be demoted to Admin and lose the
              ability to delete or rename the workspace. Billing stays linked to the
              current Stripe customer.
            </p>
            {eligibleTransferTargets.length === 0 ? (
              <p className="text-sm text-gray-500">
                Invite a teammate first — you can only transfer to an existing member.
              </p>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  data-testid="org-transfer-select"
                >
                  <option value="">Choose new owner…</option>
                  {eligibleTransferTargets.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name || m.email} ({m.role})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleTransfer}
                  disabled={transferring || !transferTo}
                  className="bg-amber-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="org-transfer-btn"
                >
                  {transferring ? 'Transferring…' : 'Transfer'}
                </button>
              </div>
            )}
          </section>
        )}

        {/* ---- Delete ---- */}
        {isOwner && (
          <section className="bg-white rounded-xl shadow-sm border border-red-200 p-5 mb-4">
            <h2 className="text-lg font-semibold text-red-700 mb-1">Delete workspace</h2>
            <p className="text-sm text-gray-600 mb-3">
              Cancel the Stripe subscription and permanently delete the workspace.
              <strong> All tasks inside this workspace are moved back to each member's
              Personal workspace</strong> — nothing is lost. This action cannot be undone.
            </p>
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-red-700 transition-colors"
                data-testid="org-delete-cta"
              >
                Delete this workspace
              </button>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 mb-3">
                  Type <strong>{org.name}</strong> to confirm deletion.
                </p>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-2.5 border border-red-300 rounded-lg mb-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder={org.name}
                  data-testid="org-delete-confirm-input"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setName(org.name); }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting || name !== org.name}
                    className="flex-1 bg-red-600 text-white py-2 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="org-delete-confirm-btn"
                  >
                    {deleting ? 'Deleting…' : 'Delete forever'}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {!isAdmin && !isOwner && (
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-sm text-gray-600">
              Workspace administration is restricted to Owners and Admins. Contact your
              workspace owner if you need a change.
            </p>
          </section>
        )}
      </div>
    </OrganizationLayout>
  );
};

export default OrganizationSettingsPage;
